import type { AxiosResponse } from 'axios'
import { errors } from './errors'

export type Pagination = {
  page: number
  totalPages: number
  total: number
}

export type Paginated<T, Meta = undefined> = {
  items: T[]
  pagination: Pagination
  meta?: Meta
}

export type SingleWithMeta<T, Meta> = {
  data: T
  meta?: Meta
}

const contractBreak = (shape: string): never => {
  throw new errors.UnexpectedEntityError({
    message: `API response is missing 'payload' (expected ${shape} envelope)`,
  })
}

const isEnvelope = (data: unknown): data is Record<string, unknown> =>
  typeof data === 'object' && data !== null

export const unwrapCollection = <T>(response: AxiosResponse): T[] => {
  const payload = response.data?.payload
  return payload ?? contractBreak('collection')
}

export const unwrapSingle = <T>(response: AxiosResponse): T => {
  const payload = response.data?.payload
  return payload ?? contractBreak('single')
}

// Some endpoints (e.g. a farm's project area) answer `{ message, payload: null }` when the
// entity legitimately does not exist yet. Absence is a valid domain state there, not a broken
// contract. An envelope with no `payload` key at all is still a contract break, so a present-but-null
// payload is the only case that yields `null`.
export const unwrapNullableSingle = <T>(response: AxiosResponse): T | null => {
  const { data } = response
  return isEnvelope(data) && 'payload' in data
    ? ((data.payload as T) ?? null)
    : contractBreak('nullable single')
}

// Some endpoints answer with a message-only envelope `{ message }` and no
// payload: v1 mutations (POST/PUT/DELETE in the monitoring module), and the v2
// eligibility-check route when it only enqueues the async detection. This
// intentionally discards the body for callers that only need the request to
// have succeeded.
export const unwrapVoid = (_response: AxiosResponse): void => {}

export const unwrapSingleWithMeta = <T, Meta>(
  response: AxiosResponse,
): SingleWithMeta<T, Meta> => ({
  data: unwrapSingle<T>(response),
  meta: response.data?.meta,
})

export const unwrapPaginated = <T, Meta = undefined>(
  response: AxiosResponse,
): Paginated<T, Meta> => {
  const { payload, page, totalPages, records, meta } = (response.data ?? {}) as {
    payload?: T[]
    page?: number
    totalPages?: number
    records?: number
    meta?: Meta
  }
  const items = payload ?? contractBreak('paginated')
  return {
    items,
    pagination: { page: page ?? 1, totalPages: totalPages ?? 1, total: records ?? items.length },
    meta,
  }
}
